import { IContextualMenuItem } from 'office-ui-fabric-react/lib/components/ContextualMenu/index';
import { ClientId, IEngagementExtraData } from '../interfaces/SharingInterfaces';
import stringsResx from '../components/Share.resx';
import * as EngagementHelper from '../utilities/EngagementHelper';
import * as ClientIdHelper from '../utilities/ClientIdHelper';

module AttachAsCopyHelper {
    /**
     * This function returns available "Attach as Copy" options for use
     * when the UI is hosted by native Office clients.
     *
     * @param clientId The clientId of the hosting application.
     * @param engagementExtraData Adding data for engagement events
     */
    export function getAttachAsCopyOptions(clientId: ClientId, engagementExtraData: IEngagementExtraData): Array<IContextualMenuItem> {
        const options = [];

        // Get document option (i.e. Word, Excel, PowerPoint) and add if available.
        const documentOption = _getDocumentOption(clientId, engagementExtraData);
        if (documentOption) {
            options.push(documentOption);
        }

        // Get PDF option and add if available.
        const pdfOption = _getPdfOption(engagementExtraData);
        if (pdfOption) {
            options.push(pdfOption);
        }

        return options;
    }

    function _getDocumentOption(clientId: ClientId, engagementExtraData: IEngagementExtraData): IContextualMenuItem {
        let isDocumentOptionAvailable = false;

        // Determine if attaching as document is supported.
        try {
            const externalJavaScript: any = window.external;
            isDocumentOptionAvailable = externalJavaScript.IsSendCopyEnabled();
        } catch (error) {
            // Nothing.
        }

        // If unavailable, just return.
        if (!isDocumentOptionAvailable) {
            return undefined;
        }

        const externalSendCopy = () => {
            _externalSendCopy(engagementExtraData);
        };

        // Return appropriate option based on clientId.
        if (ClientIdHelper.isWord(clientId)) {
            return {
                key: 'word',
                name: stringsResx.wordDocument,
                iconProps: {
                    iconName: 'WordDocument',
                    className: 'Share-wordIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isPowerPoint(clientId)) {
            return {
                key: 'powerpoint',
                name: stringsResx.powerPointPresentation,
                iconProps: {
                    iconName: 'PowerPointDocument',
                    className: 'Share-powerpointIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isExcel(clientId)) {
            return {
                key: 'excel',
                name: stringsResx.excelWorkbook,
                iconProps: {
                    iconName: 'ExcelDocument',
                    className: 'Share-excelIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isVisio(clientId)) {
            return {
                key: 'visio',
                name: stringsResx.visioDrawing,
                iconProps: { iconName: 'VisioLogo' },
                onClick: externalSendCopy
            };
        } else {
            return {
                key: 'document',
                name: stringsResx.document,
                iconProps: { iconName: 'Document' },
                onClick: externalSendCopy
            };
        }
    }

    function _getPdfOption(engagementExtraData: IEngagementExtraData): IContextualMenuItem {
        let isPdfOptionAvailable = false;

        // Determine if attaching as PDF is supported.
        try {
            const externalJavaScript: any = window.external;
            isPdfOptionAvailable = externalJavaScript.IsSendPdfEnabled();
        } catch (error) {
            // Nothing.
        }

        // If unavailable, just return.
        if (!isPdfOptionAvailable) {
            return undefined;
        }

        return {
            key: 'pdf',
            name: stringsResx.pdf,
            iconProps: {
                iconName: 'PDF',
                className: 'Share-pdfIcon'
            },
            onClick: () => {
                _externalSendPdf(engagementExtraData);
            }
        };
    }

    function _externalSendCopy(engagementExtraData: IEngagementExtraData) {
        EngagementHelper.sendACopyOfficeClicked(engagementExtraData);
        try {
            const externalJavaScript: any = window.external;
            externalJavaScript.SendCopy();
        } catch (error) {
            // Nothing.
        }
    }

    function _externalSendPdf(engagementExtraData: IEngagementExtraData) {
        EngagementHelper.sendACopyPdfClicked(engagementExtraData);
        try {
            const externalJavaScript: any = window.external;
            externalJavaScript.SendPdf();
        } catch (error) {
            // Nothing.
        }
    }
}

export default AttachAsCopyHelper;